/**
 * A teacher's markup on a child's transcript (总结 recordings, AI answers):
 * the text she corrected the speech recognition to, and marks on its words.
 *
 *   { text: string, marks: [{ start, end, style, comment }] }
 *
 * `start`/`end` are inclusive word indexes into markupWords(text). `style` is
 * 'highlight' | 'strike' | null (a comment alone); `comment` is her note on
 * those words, or null. Marks never overlap and are kept in reading order.
 *
 * Words, not characters: she picks them by tapping, and a whole-word anchor
 * can be carried across an edit of the text (realignMarks). The backend checks
 * the same shape — see transcriptMarkupSchema in requestSchemas.js.
 */

export const MARK_STYLES = ['highlight', 'strike'];

/** The words marks index into. Punctuation stays on its word. */
export function markupWords(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean);
}

/** Saved markup the child should be shown. */
export function hasMarkup(markup) {
  return !!markup && markupWords(markup.text).length > 0;
}

const byStart = (a, b) => a.start - b.start;

/**
 * Set the style and/or comment on words start..end. The same range updates
 * its mark; any other mark it overlaps is replaced. A mark left with neither
 * a style nor a comment is removed.
 *
 * @param {Array} marks
 * @param {number} from
 * @param {number} to
 * @param {{style?: ?string, comment?: ?string}} patch
 */
export function setMark(marks, from, to, patch) {
  const start = Math.min(from, to);
  const end = Math.max(from, to);
  const same = marks.find((m) => m.start === start && m.end === end);
  const others = marks.filter((m) => m !== same && (m.end < start || m.start > end));
  const next = {
    start,
    end,
    style: patch.style !== undefined ? patch.style : (same?.style ?? null),
    comment: patch.comment !== undefined ? (patch.comment?.trim() || null) : (same?.comment ?? null),
  };
  if (!next.style && !next.comment) return others.sort(byStart);
  return [...others, next].sort(byStart);
}

/**
 * Carry marks across an edit of the text. Words the edit left alone keep
 * their marks at their new positions (longest common subsequence of the two
 * word lists); a mark loses out if any of its words was changed or removed.
 *
 * @returns {{marks: Array, dropped: number}}
 */
export function realignMarks(oldText, newText, marks) {
  const a = markupWords(oldText);
  const b = markupWords(newText);
  // LCS table, then walk it to map each kept old word to its new index.
  const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const newIndexOf = new Map();
  for (let i = 0, j = 0; i < a.length && j < b.length;) {
    if (a[i] === b[j]) { newIndexOf.set(i, j); i++; j++; } else if (lcs[i + 1][j] >= lcs[i][j + 1]) i++; else j++;
  }

  const kept = [];
  (marks || []).forEach((m) => {
    const mapped = [];
    for (let i = m.start; i <= m.end; i++) mapped.push(newIndexOf.get(i));
    // Every word survived, still side by side.
    const whole = mapped.every((j, k) => j !== undefined && (k === 0 || j === mapped[k - 1] + 1));
    if (whole) kept.push({ ...m, start: mapped[0], end: mapped[mapped.length - 1] });
  });
  return { marks: kept, dropped: (marks || []).length - kept.length };
}

/**
 * The markup for the parent-message export, which is plain text: the
 * corrected text, then one line per strike or comment. A highlight with no
 * comment can't be shown in plain text, so it is left out. `withTranscript:
 * false` drops the 说的是 line and keeps only the marks (the 总结 export).
 */
export function markupExportLines(markup, { withTranscript = true } = {}) {
  if (!hasMarkup(markup)) return [];
  const words = markupWords(markup.text);
  const lines = withTranscript ? [`说的是: ${words.join(' ')}`] : [];
  (markup.marks || []).forEach((m) => {
    const span = `「${words.slice(m.start, m.end + 1).join(' ')}」`;
    if (m.style === 'strike') lines.push(m.comment ? `${span}→ ${m.comment}` : `${span}删掉`);
    else if (m.comment) lines.push(`${span}: ${m.comment}`);
  });
  return lines;
}

/** A stored markup column (JSON text) or an already-parsed object → object or null. */
export function parseMarkup(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

/**
 * The text in reading order as runs: plain words between marks, and each mark
 * with its words. What both the child's view and the teacher's annotator draw.
 *
 * @returns {Array<{start: number, end: number, words: string[], mark: ?object}>}
 */
export function markupRuns(markup) {
  const words = markupWords(markup?.text);
  const runs = [];
  let i = 0;
  [...(markup?.marks || [])].sort(byStart).forEach((mark) => {
    if (mark.start > i) runs.push({ start: i, end: mark.start - 1, words: words.slice(i, mark.start), mark: null });
    runs.push({ start: mark.start, end: mark.end, words: words.slice(mark.start, mark.end + 1), mark });
    i = mark.end + 1;
  });
  if (i < words.length) runs.push({ start: i, end: words.length - 1, words: words.slice(i), mark: null });
  return runs;
}
